import { useEffect, useMemo, useState } from "react";
import { ArrowRight, ChefHat, Clock3, Leaf, ShieldCheck, Sparkles, Sunrise } from "lucide-react";
import { AboutPage, Header, ImprintPage, ProfileCard, Questionnaire, RecipeLibrary, RitualLibrary } from "./components";
import { CoachPage } from "./CoachPage";
import { clearCoachData } from "./coach";
import { I18nProvider } from "./i18n";
import { clearProfile, DOSHA_META, loadBootstrap, readProfile } from "./lib";
import { PrivacyPage } from "./PrivacyPage";
import { SHOPPING_STORAGE_KEY } from "./shopping";
import type { BootstrapData, DoshaProfile, View } from "./types";

function LoadingScreen() {
  return <main className="loading-screen"><img src="/icon-192.png" alt="" /><span /><p>Dein Raum wird vorbereitet …</p></main>;
}

function ErrorScreen({ retry }: { retry: () => void }) {
  return <main className="error-screen"><img src="/icon-192.png" alt="" /><p className="eyebrow">Einen Moment bitte</p><h1>Die Inhalte konnten nicht geladen werden.</h1><p>Prüfe deine Verbindung oder versuche es gleich noch einmal.</p><button className="primary-button" onClick={retry}>Erneut versuchen</button></main>;
}

export default function App() {
  const [data, setData] = useState<BootstrapData | null>(null);
  const [view, setView] = useState<View>("home");
  const [profile, setProfile] = useState<DoshaProfile | null>(() => readProfile());
  const [error, setError] = useState(false);
  const [loadKey, setLoadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setError(false);
    loadBootstrap(controller.signal).then(setData).catch((cause) => {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setError(true);
    });
    return () => controller.abort();
  }, [loadKey]);

  useEffect(() => {
    const handler = () => setView((location.hash.replace("#", "") as View) || "home");
    window.addEventListener("hashchange", handler);
    handler();
    return () => window.removeEventListener("hashchange", handler);
  }, []);

  const navigate = (next: View) => {
    location.hash = next === "home" ? "" : next;
    setView(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const recommendedRecipes = useMemo(() => {
    if (!data) return [];
    if (!profile) return data.recipes.slice(0, 3);
    return data.recipes.filter((recipe) => recipe.doshas.includes(profile.primary)).slice(0, 3);
  }, [data, profile]);

  if (error) return <ErrorScreen retry={() => setLoadKey((value) => value + 1)} />;
  if (!data) return <LoadingScreen />;
  const t = (key: string, fallback: string) => data.translations[key] ?? fallback;
  if (view === "quiz") return <I18nProvider translations={data.translations}><Questionnaire questions={data.questions} version={data.appVersion} onCancel={() => navigate("home")} onComplete={(nextProfile) => { setProfile(nextProfile); navigate("home"); }} /></I18nProvider>;

  return (
    <I18nProvider translations={data.translations}>
    <div className="app-shell">
      <Header view={view} onNavigate={navigate} />
      {view === "coach" && <CoachPage recipes={data.recipes} profile={profile} seasonGuides={data.seasonGuides ?? []} routineItems={data.routineItems ?? []} onNavigate={navigate} />}
      {view === "home" && (
        <main>
          <section className="hero">
            <img className="hero-image" src="/assets/ayurvedon-hero.png" alt="Eine warme Schale mit Kitchari, Gewürzen und botanischen Blättern" />
            <div className="hero-overlay" />
            <div className="hero-content">
              <p className="eyebrow">{t("brand.claim", "Im eigenen Rhythmus")}</p>
              <h1>{t("home.hero.title.before", "Was tut dir")}<br /><em>{t("home.hero.title.emphasis", "heute")}</em> {t("home.hero.title.after", "gut?")}</h1>
              <p>{t("home.hero.body", "Entdecke deine ayurvedische Konstitution und finde sanfte Impulse für Küche, Ruhe und deinen ganz persönlichen Alltag.")}</p>
              <button className="primary-button" onClick={() => navigate(profile ? "coach" : "quiz")}>
                {profile ? t("home.hero.ctaProfile", "Meinen Tag ansehen") : t("home.hero.cta", "Dosha-Reflexion starten")} <ArrowRight />
              </button>
              {!profile && <span className="hero-time"><Clock3 /> 5–7 Minuten · lokal gespeichert</span>}
            </div>
          </section>

          <section className="home-section intro-section">
            {profile ? (
              <ProfileCard profile={profile} />
            ) : (
              <div className="intro-copy"><p className="eyebrow">Vata · Pitta · Kapha</p><h2>{t("home.intro.titleFirst", "Drei Qualitäten.")}<br />{t("home.intro.titleSecond", "Dein einzigartiges Zusammenspiel.")}</h2><p>{t("home.intro.body", "Ayurveda betrachtet Wohlbefinden als ein bewegliches Gleichgewicht. Unser ausführlicher Fragebogen hilft dir, deine Tendenzen bewusst wahrzunehmen.")}</p></div>
            )}
            <div className="principle-grid">
              {(Object.keys(DOSHA_META) as (keyof typeof DOSHA_META)[]).map((dosha) => <article key={dosha}><i className={`dosha-${dosha}`}><Sparkles /></i><h3>{DOSHA_META[dosha].label}</h3><p>{DOSHA_META[dosha].essence}</p></article>)}
            </div>
          </section>

          <section className="home-section recommendation-section">
            <div className="section-heading"><div><p className="eyebrow">{t("home.recommendations.eyebrow", "Für dich ausgewählt")}</p><h2>{t("home.recommendations.title", "Wärme, die von innen kommt")}</h2></div><button className="text-button" onClick={() => navigate("recipes")}>{t("home.recommendations.all", "Alle Rezepte")} <ArrowRight /></button></div>
            <div className="featured-grid">
              {recommendedRecipes.map((recipe, index) => <article key={recipe.slug} className={index === 0 ? "featured" : ""} onClick={() => navigate("recipes")}>
                <img src={recipe.image} alt="" loading="lazy" /><div><span>{recipe.doshas.map((dosha) => DOSHA_META[dosha].label).join(" · ")}</span><h3>{recipe.title}</h3><p>{recipe.subtitle}</p><small><Clock3 /> {recipe.minutes} Min.</small></div>
              </article>)}
            </div>
          </section>

          <section className="ritual-teaser">
            <div><p className="eyebrow">{t("home.ritual.eyebrow", "Ein Moment für dich")}</p><h2>{t("home.ritual.titleFirst", "Rituale müssen nicht groß sein,")}<br /><em>{t("home.ritual.titleSecond", "um etwas zu verändern.")}</em></h2><p>{t("home.ritual.body", "Ein warmer Morgen, drei ruhige Atemzüge, ein bewusster Übergang in den Abend. Beginne dort, wo du gerade bist.")}</p><button className="light-button" onClick={() => navigate("rituals")}>{t("home.ritual.cta", "Rituale entdecken")} <ArrowRight /></button></div>
            <div className="ritual-orbit"><Sunrise /><span><Leaf /></span></div>
          </section>

          <section className="trust-strip"><div><ShieldCheck /></div><p><b>{t("home.privacy.title", "Deine Daten bleiben bei dir.")}</b>{t("home.privacy.body", "Antworten und Ergebnis werden ausschließlich lokal auf diesem Gerät gespeichert. Keine Registrierung, kein Tracking.")}</p></section>
        </main>
      )}
      {view === "recipes" && <RecipeLibrary recipes={data.recipes} profile={profile} />}
      {view === "rituals" && <RitualLibrary rituals={data.rituals} profile={profile} />}
      {view === "about" && <AboutPage releases={data.releases} version={data.appVersion} onClear={() => {
        clearProfile();
        clearCoachData();
        localStorage.removeItem(SHOPPING_STORAGE_KEY);
        setProfile(null);
      }} />}
      {view === "privacy" && <PrivacyPage notice={data.legalNotice ?? null} />}
      {view === "imprint" && <ImprintPage notice={data.legalNotice ?? null} />}
      <footer><div className="brand footer-brand"><img src="/favicon-64.png" alt="" /><span>Ayurvedon</span></div><p>{t("footer.claim", "Traditionelle Inspiration. Zeitgemäß und achtsam eingeordnet.")}</p><nav aria-label={t("footer.legalNavigation", "Rechtliche Navigation")}><a href="#about">{t("footer.about", "Über Ayurvedon")}</a><a href="#privacy">{t("footer.privacy", "Datenschutz")}</a><a href="#imprint">{t("footer.imprint", "Impressum")}</a></nav><span>v{data.appVersion}</span></footer>
    </div>
    </I18nProvider>
  );
}
